import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, FileText, AlertTriangle, TrendingUp, Building2, Pill, List, BarChart3, Syringe, TestTube } from 'lucide-react';
import { toast } from 'sonner';
import InteractiveChart from '../components/InteractiveChart';
import ExamStatistics from '../components/ExamStatistics';

const COLORS = ['#0D9488', '#8B5CF6', '#E9604A', '#F59E0B', '#10B981', '#3B82F6'];
const VACCINE_COLORS = {
  complete: '#22c55e',
  in_progress: '#3b82f6',
  pending: '#9ca3af',
  overdue: '#ef4444'
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vaccineStats, setVaccineStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [healthUnits, setHealthUnits] = useState([]);
  const [deliveryDates, setDeliveryDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (currentUser.role !== 'administrador' && currentUser.role !== 'auditor') {
      toast.error('Sem permissão para acessar');
      navigate('/');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, vaccineStatsRes, patientsRes, prescRes, unitsRes, deliveryRes] = await Promise.all([
        axiosInstance.get('/admin/dashboard-stats'),
        axiosInstance.get('/vaccination-stats-by-vaccine').catch(() => ({ data: null })),
        axiosInstance.get('/patients'),
        axiosInstance.get('/prescriptions'),
        axiosInstance.get('/health-units'),
        axiosInstance.get('/delivery-dates/all')
      ]);
      setStats(statsRes.data);
      setVaccineStats(vaccineStatsRes.data);
      setPatients(patientsRes.data || []);
      setPrescriptions(prescRes.data || []);
      setHealthUnits(unitsRes.data || []);
      setDeliveryDates(deliveryRes.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar dashboard');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] flex items-center justify-center">
        <p className="text-lg text-muted-foreground">Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)]">
      <div className="max-w-7xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-2">
            Dashboard Administrativo
          </h1>
          <p className="text-muted-foreground font-medium">Visão geral e estatísticas do sistema</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Pacientes</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_patients || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receitas Este Mês</CardTitle>
              <FileText className="h-4 w-4 text-secondary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.prescriptions_this_month || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">A Vencer (30 dias)</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-500">{stats?.expiring_soon || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receitas Expiradas</CardTitle>
              <TrendingUp className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-500">{stats?.expired_prescriptions || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Charts and Medication Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Patients by Unit */}
          <Card>
            <CardHeader>
              <CardTitle>Pacientes por Unidade</CardTitle>
              <CardDescription>Distribuição de pacientes nas unidades de saúde</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats?.patients_by_unit || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => entry.unit_name}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {(stats?.patients_by_unit || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Medications (Basic) */}
          <Card>
            <CardHeader>
              <CardTitle>Medicamentos Mais Prescritos</CardTitle>
              <CardDescription>Top 10 medicamentos (por nome)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.top_medications || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0D9488" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Medication Analysis Tabs */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="w-5 h-5" />
              Análise Detalhada de Medicamentos
            </CardTitle>
            <CardDescription>
              Medicamentos separados por dosagem e lista completa com frequência
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="with-dosage" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="with-dosage" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Por Dosagem
                </TabsTrigger>
                <TabsTrigger value="complete-list" className="flex items-center gap-2">
                  <List className="w-4 h-4" />
                  Lista Completa
                </TabsTrigger>
                <TabsTrigger value="by-medication" className="flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  Por Medicamento
                </TabsTrigger>
              </TabsList>

              {/* Medications with Dosage */}
              <TabsContent value="with-dosage" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Top 15 Medicamentos com Dosagem</h3>
                    <Badge variant="secondary">
                      {stats?.top_medications_with_dosage?.length || 0} medicamentos
                    </Badge>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={stats?.top_medications_with_dosage || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={120}
                        fontSize={12}
                      />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>

              {/* Complete List */}
              <TabsContent value="complete-list" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Lista Completa de Medicamentos</h3>
                    <Badge variant="secondary">
                      {stats?.total_unique_medications || 0} medicamentos únicos
                    </Badge>
                  </div>
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    <div className="grid gap-2 p-4">
                      {(stats?.all_medications_list || []).map((med, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">
                                {index + 1}
                              </span>
                            </div>
                            <span className="font-medium text-sm">{med.name}</span>
                          </div>
                          <Badge variant="outline" className="font-mono">
                            {med.count}x
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* By Medication (Dosage breakdown) */}
              <TabsContent value="by-medication" className="mt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Medicamentos por Dosagem</h3>
                    <Badge variant="secondary">
                      Detalhamento por dosagem
                    </Badge>
                  </div>
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    <div className="space-y-4 p-4">
                      {Object.entries(stats?.medications_by_dosage || {}).map(([medication, dosages]) => (
                        <div key={medication} className="border-l-4 border-l-primary pl-4">
                          <h4 className="font-semibold text-primary mb-2">{medication}</h4>
                          <div className="grid gap-2">
                            {Object.entries(dosages).map(([dosage, count]) => (
                              <div 
                                key={dosage}
                                className="flex items-center justify-between p-2 bg-gray-50 rounded"
                              >
                                <span className="text-sm font-medium">{dosage}</span>
                                <Badge variant="outline" className="font-mono text-xs">
                                  {count}x
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Vaccination Statistics Card */}
        {vaccineStats && (
          <Card className="mt-6 shadow-lg border-0 bg-white/70 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Syringe className="w-5 h-5 text-primary" />
                Estatísticas de Vacinação
              </CardTitle>
              <CardDescription>
                Status de vacinação por vacina ({vaccineStats.total_patients} pacientes)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                  <p className="text-3xl font-bold text-green-600">{vaccineStats.summary?.total_complete || 0}</p>
                  <p className="text-sm text-green-700">Em Dia</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                  <p className="text-3xl font-bold text-blue-600">{vaccineStats.summary?.total_in_progress || 0}</p>
                  <p className="text-sm text-blue-700">Em Andamento</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                  <p className="text-3xl font-bold text-gray-600">{vaccineStats.summary?.total_pending || 0}</p>
                  <p className="text-sm text-gray-700">Pendentes</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
                  <p className="text-3xl font-bold text-red-600">{vaccineStats.summary?.total_overdue || 0}</p>
                  <p className="text-sm text-red-700">Atrasadas</p>
                </div>
              </div>

              {/* Stacked Bar Chart */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Gráfico de Status por Vacina</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={vaccineStats.vaccines_stats?.slice(0, 15) || []}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 150, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="vaccine_name" 
                      tick={{ fontSize: 11 }}
                      width={140}
                    />
                    <Tooltip 
                      formatter={(value, name) => {
                        const labels = {
                          complete: 'Em dia',
                          in_progress: 'Em andamento',
                          pending: 'Pendentes',
                          overdue: 'Atrasadas'
                        };
                        return [value, labels[name] || name];
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const labels = {
                          complete: 'Em dia',
                          in_progress: 'Em andamento',
                          pending: 'Pendentes',
                          overdue: 'Atrasadas'
                        };
                        return labels[value] || value;
                      }}
                    />
                    <Bar dataKey="complete" stackId="a" fill={VACCINE_COLORS.complete} name="complete" />
                    <Bar dataKey="in_progress" stackId="a" fill={VACCINE_COLORS.in_progress} name="in_progress" />
                    <Bar dataKey="pending" stackId="a" fill={VACCINE_COLORS.pending} name="pending" />
                    <Bar dataKey="overdue" stackId="a" fill={VACCINE_COLORS.overdue} name="overdue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Detailed Table */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Detalhamento por Vacina</h3>
                <ScrollArea className="h-[400px] border rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-600">Vacina</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-600">Tipo</th>
                        <th className="text-center p-3 text-sm font-medium text-green-600">✓ Em Dia</th>
                        <th className="text-center p-3 text-sm font-medium text-blue-600">⏳ Andamento</th>
                        <th className="text-center p-3 text-sm font-medium text-gray-500">○ Pendentes</th>
                        <th className="text-center p-3 text-sm font-medium text-red-600">⚠️ Atrasadas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vaccineStats.vaccines_stats?.map((vaccine, idx) => (
                        <tr key={vaccine.vaccine_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3 text-sm font-medium">
                            {vaccine.vaccine_name}
                            {vaccine.abbreviation && (
                              <span className="text-xs text-gray-400 ml-1">({vaccine.abbreviation})</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline" className={
                              vaccine.type === 'PNI' ? 'border-blue-300 text-blue-700' :
                              vaccine.type === 'SBIM' ? 'border-purple-300 text-purple-700' :
                              'border-indigo-300 text-indigo-700'
                            }>
                              {vaccine.type}
                            </Badge>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              vaccine.complete > 0 ? 'bg-green-100 text-green-700' : 'text-gray-400'
                            }`}>
                              {vaccine.complete}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              vaccine.in_progress > 0 ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
                            }`}>
                              {vaccine.in_progress}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              vaccine.pending > 0 ? 'bg-gray-100 text-gray-600' : 'text-gray-400'
                            }`}>
                              {vaccine.pending}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              vaccine.overdue > 0 ? 'bg-red-100 text-red-700' : 'text-gray-400'
                            }`}>
                              {vaccine.overdue}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Exam Statistics Section */}
        <Card className="mt-6 shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5 text-teal-600" />
              Estatísticas de Exames Solicitados
            </CardTitle>
            <CardDescription>
              Análise de exames mais solicitados por período, unidade e categoria
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ExamStatistics healthUnits={healthUnits} />
          </CardContent>
        </Card>

        {/* Interactive Chart - Gráfico Personalizado */}
        <div className="mt-8">
          <InteractiveChart
            patients={patients}
            prescriptions={prescriptions}
            healthUnits={healthUnits}
            deliveryDates={deliveryDates}
            title="Análise Gerencial Personalizada"
            description="Configure os parâmetros para criar análises personalizadas da sua unidade de saúde"
          />
        </div>
      </div>
    </div>
  );
}